(() => {
  'use strict';
  const catalogs = new Map();
  const panels = new Set();
  // Keep the same two-second messages while avoiding animation work on distant cards.
  const benefitVisibility = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        entry.target.dataset.omMotion = entry.isIntersecting ? 'running' : 'paused';
      });
    }, { rootMargin: '150px' })
    : null;
  // Resolve every catalog page before exposing counts; never count just visible cards.
  function catalog(url) {
    if (!catalogs.has(url)) {
      const task = (async () => {
        const products = new Map();
        const visited = new Set();
        let next = new URL(url, location.origin);
        while (next) {
          if (next.origin !== location.origin || visited.has(next.href)) throw new Error('Invalid catalog pagination');
          visited.add(next.href);
          const response = await fetch(next.href, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalog unavailable');
          const data = await response.json();
          if (!Array.isArray(data.products)) throw new Error('Invalid catalog');
          for (const product of data.products) products.set(String(product.id), product);
          next = data.next ? new URL(data.next, location.origin) : null;
          if (next) next.searchParams.set('view', 'onlymoon-colors');
          if (!next && Number(data.total) !== products.size) throw new Error('Incomplete catalog');
        }
        const groups = new Map();
        for (const product of products.values()) {
          if (!product.model) continue;
          if (!groups.has(product.model)) groups.set(product.model, []);
          groups.get(product.model).push(product);
        }
        return groups;
      })();
      catalogs.set(url, task);
      task.catch(() => catalogs.delete(url));
    }
    return catalogs.get(url);
  }
  function setupColors(root) {
    if (root.dataset.omInitialized) return;
    root.dataset.omInitialized = 'true';
    catalog(root.dataset.catalogUrl).then(groups => {
      if (!root.isConnected) return;
      const group = groups.get(root.dataset.model) || [];
      const active = group.find(product => String(product.id) === root.dataset.productId);
      if (group.length < 2 || !active) return;
      const products = [active, ...group.filter(product => product !== active)];
      const badge = root.querySelector('.om-color-badge');
      let panel, list, more;
      let timer;
      let hoverTimer;
      let opening = false;
      let offset = 0;
      const refresh = () => {
        clearTimeout(timer);
        if (panel && !panel.hidden) timer = setTimeout(close, 5000);
      };
      function close() {
        clearTimeout(timer);
        if (panel) panel.hidden = true;
        badge.hidden = false;
        badge.setAttribute('aria-expanded', 'false');
        panels.delete(close);
      }
      function ensurePanel() {
        if (panel) return;
        panel = document.createElement('div');
        panel.className = 'om-color-panel';
        panel.id = badge.getAttribute('aria-controls');
        panel.hidden = true;
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-label', 'Farklı Renk Seçenekleri');
        panel.innerHTML = '<div class="om-color-heading"><span>Farklı Renk Seçenekleri</span><button type="button" class="om-color-close" aria-label="Renk seçeneklerini kapat">×</button></div><div class="om-color-products"></div><button type="button" class="om-color-more" hidden></button>';
        root.append(panel);
        list = panel.querySelector('.om-color-products');
        more = panel.querySelector('.om-color-more');
        panel.addEventListener('click', event => { event.stopPropagation(); refresh(); });
        ['pointermove', 'pointerdown', 'focusin', 'keydown'].forEach(type => panel.addEventListener(type, refresh));
        panel.addEventListener('keydown', event => {
          if (event.key === 'Escape') { close(); badge.focus(); }
        });
        panel.querySelector('.om-color-close').addEventListener('click', close);
        more.addEventListener('click', () => { offset = offset + 4 < products.length ? offset + 4 : 0; render(); refresh(); });
      }
      function render() {
        const ready = [];
        list.replaceChildren();
        products.slice(offset, offset + 4).forEach(product => {
          const link = document.createElement('a');
          link.href = product.url;
          link.title = product.title;
          link.setAttribute('aria-label', product.title);
          if (String(product.id) === root.dataset.productId) link.setAttribute('aria-current', 'page');
          if (product.image) {
            const image = new Image();
            image.src = product.image;
            image.alt = product.title;
            image.loading = 'eager';
            image.decoding = 'async';
            link.style.backgroundImage = `url("${image.src}")`;
            ready.push(image.decode().catch(() => {}));
          } else link.textContent = product.title;
          list.append(link);
        });
        const remaining = products.length - offset - 4;
        more.hidden = products.length <= 4;
        more.textContent = remaining > 0 ? `+${remaining} Renk ›` : '‹ İlk Renkler';
        return Promise.all(ready);
      }
      const open = async () => {
        ensurePanel();
        if (!panel.hidden) { refresh(); return; }
        if (opening) return;
        opening = true;
        badge.setAttribute('aria-busy', 'true');
        panels.forEach(closeOther => closeOther());
        offset = 0;
        await render();
        opening = false;
        badge.removeAttribute('aria-busy');
        if (!root.isConnected) return;
        panels.forEach(closeOther => closeOther());
        panel.hidden = false;
        badge.hidden = true;
        badge.setAttribute('aria-expanded', 'true');
        panels.add(close);
        refresh();
      };
      root.querySelector('[data-om-count]').textContent = products.length;
      badge.setAttribute('aria-label', `${products.length} Farklı Renk Seçeneği`);
      badge.hidden = false;
      badge.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') hoverTimer = setTimeout(open, 150);
      });
      badge.addEventListener('pointerleave', () => clearTimeout(hoverTimer));
      badge.addEventListener('click', event => { clearTimeout(hoverTimer); event.preventDefault(); event.stopPropagation(); open(); });
    }).catch(error => {
      // Missing/incomplete data must never produce a misleading color count.
      delete root.dataset.omInitialized;
      console.warn('OnlyMoon renk kataloğu yüklenemedi:', error.message);
    });
  }
  function setupGallery(root) {
    if (root.dataset.omGalleryReady) return;
    root.dataset.omGalleryReady = 'true';
    const photos = [...new Set(JSON.parse(root.querySelector('[data-om-gallery-images]').textContent))];
    const box = root.closest('.bee-product-image');
    const original = box.querySelector('img.bee-pr-img');
    const link = box.querySelector('.bee-product-link');
    const dots = root.querySelector('.om-gallery-dots');
    const mobile = window.matchMedia('(max-width:767px)');
    let track, returnTimer, touching = false;
    function scheduleReturn() {
      clearTimeout(returnTimer);
      if (touching || !mobile.matches || !track || track.scrollLeft < 1) return;
      returnTimer = setTimeout(() => {
        if (!touching && mobile.matches && track && box.isConnected) {
          track.scrollTo({left:0, behavior:'smooth'});
        }
      }, 3000);
    }
    box.addEventListener('pointerdown', () => {
      touching = true;
      clearTimeout(returnTimer);
    }, {passive:true});
    const release = () => { touching = false; scheduleReturn(); };
    box.addEventListener('pointerup', release, {passive:true});
    box.addEventListener('pointercancel', release, {passive:true});
    box.addEventListener('pointerleave', release, {passive:true});
    mobile.addEventListener('change', () => { clearTimeout(returnTimer); touching = false; });
    const buttons = photos.map((photo, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', `Ürün fotoğrafı ${i + 1}`);
      button.setAttribute('aria-current', String(i === 0));
      button.addEventListener('click', event => {
        event.preventDefault(); event.stopPropagation();
        if (mobile.matches && track) track.scrollTo({left:i * track.clientWidth, behavior:'smooth'});
      });
      dots.append(button);
      return button;
    });
    function activate() {
      if (!mobile.matches || track) return;
      track = document.createElement('div');
      track.className = 'om-gallery-track';
      track.setAttribute('aria-label', 'Ürün fotoğrafları');
      photos.forEach((photo, i) => {
        const slide = document.createElement('a');
        slide.className = 'om-gallery-item';
        slide.href = link.href;
        slide.setAttribute('aria-label', `${original.alt} — fotoğraf ${i + 1}`);
        const image = document.createElement('img');
        image.alt = original.alt;
        image.draggable = false;
        image.loading = 'eager';
        image.fetchPriority = i === 0 ? 'auto' : 'low';
        image.decoding = 'async';
        image.sizes = original.sizes;
        image.srcset = [180,240,320,350,400,480,600,750,900].map(w => {
          const url = new URL(photo, location.origin);
          url.searchParams.set('width', w);
          return `${url.href} ${w}w`;
        }).join(', ');
        image.src = photo;
        slide.append(image);
        track.append(slide);
        image.decode().catch(() => {});
      });
      let frame;
      track.addEventListener('scroll', () => {
        scheduleReturn();
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          const index = Math.round(track.scrollLeft / track.clientWidth);
          buttons.forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
        });
      }, {passive:true});
      box.insertBefore(track, root);
      box.classList.add('om-gallery-native');
    }
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && mobile.matches) {
          activate(); observer.disconnect();
        }
      }, {rootMargin:'150px'});
      observer.observe(box);
      mobile.addEventListener('change', () => {
        if (mobile.matches && !track) observer.observe(box);
      });
    } else { activate(); mobile.addEventListener('change', activate); }
  }
  function scan(scope = document) {
    if (scope.matches?.('[data-om-gallery]')) setupGallery(scope);
    scope.querySelectorAll?.('[data-om-gallery]').forEach(setupGallery);
    if (scope.matches?.('[data-om-colors]')) setupColors(scope);
    scope.querySelectorAll?.('[data-om-colors]').forEach(setupColors);
    const observeBenefit = el => {
      if (!benefitVisibility || el.dataset.omMotion) return;
      el.dataset.omMotion = 'paused';
      benefitVisibility.observe(el);
    };
    if (scope.matches?.('[data-om-benefits]')) observeBenefit(scope);
    scope.querySelectorAll?.('[data-om-benefits]').forEach(observeBenefit);
    scope.querySelectorAll?.('.jdgm-prev-badge__text:not([data-om-review-ready])').forEach(el => {
      el.dataset.omReviewReady = 'true';
      const number = el.textContent.match(/\d+/);
      el.textContent = number ? '(' + number[0] + ')' : '';
    });
  }
  const start = () => {
    scan();
    document.addEventListener('click', event => {
      if (event.target.closest('.bee-review-scroll')) event.stopPropagation();
    });
    const pending = new Set();
    let queued = false;
    new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) {
        if (node.nodeType === 1) pending.add(node);
      }
      if (queued || !pending.size) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        pending.forEach(node => { if (node.isConnected) scan(node); });
        pending.clear();
      });
    }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
